import React, { useMemo, useState } from 'react';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { Radio, Spin } from 'antd';
import { useRequest } from 'ahooks';
import {
  apiV2HighEnergyDeviceAnalysisV2EnergyConsumptionPieChartPost,
  apiV2HighEnergyDeviceAnalysisV2EnergyCostPieChartPost,
} from '@maxtropy/device-customer-apis-v2';
import { formatThousandUnitDefaultTwo, keepTwoOrLine, keepTwoAndWanOrLine } from '@/utils/utils';
import { getChartOption, PieLegendItem } from './chart';
import { DateType } from '../TimeSelector';
import styles from './index.module.scss';

// TimeParams interface definition
export interface TimeParams {
  startTime: number;
  endTime: number;
  timeResolution: DateType;
}

export type { PieLegendItem } from './chart';

export interface EnergyCardProps {
  timeParams: TimeParams;
}

type EnergyType = 'energy' | 'cost';

const COLORS = ['#FF4D4F', '#FFCB47', '#2D8DFF', '#16DD8E', '#ABD335'];

const EnergyCard: React.FC<EnergyCardProps> = ({ timeParams }) => {
  const { startTime, endTime, timeResolution } = timeParams;

  const theme = useTheme();
  const [energyType, setEnergyType] = useState<EnergyType>('energy');

  // 用能数据接口
  const { data: energyData, loading: energyLoading } = useRequest(
    () =>
      apiV2HighEnergyDeviceAnalysisV2EnergyConsumptionPieChartPost({
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        timeResolution,
      }),
    {
      ready: !!startTime && !!endTime && energyType === 'energy',
      refreshDeps: [startTime, endTime, timeResolution],
    }
  );

  // 用能成本数据接口
  const { data: costData, loading: costLoading } = useRequest(
    () =>
      apiV2HighEnergyDeviceAnalysisV2EnergyCostPieChartPost({
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        timeResolution,
      }),
    {
      ready: !!startTime && !!endTime && energyType === 'cost',
      refreshDeps: [startTime, endTime, timeResolution, energyType],
    }
  );

  const loading = energyType === 'energy' ? energyLoading : costLoading;
  const data = energyType === 'energy' ? energyData : costData;

  const pieLegendData: PieLegendItem[] = useMemo(() => {
    return (data?.pieChartData ?? []).map((item, index) => ({
      name: item.deviceTypeName ?? '--',
      value: energyType === 'cost' ? (item as any).cost : (item as any).consumption,
      rate: item.percentage,
      color: COLORS[index % COLORS.length],
    }));
  }, [data, energyType]);

  const chartOption = useMemo(() => {
    return getChartOption(pieLegendData, energyType);
  }, [pieLegendData, energyType]);

  const isEmpty = !pieLegendData || pieLegendData.length === 0;

  const unit = energyType === 'energy' ? 'kWh' : '元';

  const handleEnergyTypeChange = (e: any) => {
    setEnergyType(e.target.value);
  };

  return (
    <div className={styles.energyCard}>
      <div className={styles.header}>
        <span className={styles.label}>高能耗设备统计</span>
        <Radio.Group
          style={{ height: '100%' }}
          value={energyType}
          onChange={handleEnergyTypeChange}
          buttonStyle="solid"
        >
          <Radio.Button value="energy">用能</Radio.Button>
          <Radio.Button value="cost">用能成本</Radio.Button>
        </Radio.Group>
      </div>
      {isEmpty ? (
        <div className={styles.emptyContent}>
          <Empty />
        </div>
      ) : (
        <Spin spinning={loading}>
          <div className={styles.content}>
            <div className={styles.chartWrapper}>
              <EchartsWithTheme
                loading={loading}
                type="pie"
                theme={theme}
                style={{ height: 180 }}
                option={chartOption}
              />
            </div>
            <div className={styles.customerLegend}>
              {(pieLegendData ?? []).map(item => (
                <div className={styles.legendItem} key={item.name}>
                  <div className={styles.icon} style={{ backgroundColor: item.color }}></div>
                  <div className={styles.name}>{item.name ?? '--'}</div>
                  <div className={styles.value}>
                    {keepTwoAndWanOrLine(item.value)}
                    {unit}
                  </div>
                  <div className={styles.rate}>{keepTwoOrLine(item.rate)}%</div>
                </div>
              ))}
            </div>
          </div>
        </Spin>
      )}
    </div>
  );
};

export default EnergyCard;
